import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { subscriptionsApi, type FinancialSummary } from '../../api/modules/subscriptions';
import { adminApi, type PlanCatalogUpdate } from '../../api/modules/admin';
import { AdminDashboardHeader } from '../AdminDashboard/AdminDashboardHeader';
import { FinancialSummaryView } from './FinancialSummaryView';
import { SubscriptionPlansCard } from '../AdminDashboard/SubscriptionPlansCard';
import { EditPlanModal } from '../AdminDashboard/EditPlanModal';

function errorMessage(error: unknown): string {
  if (typeof error === 'object' && error !== null) {
    const withResponse = error as { response?: { data?: { error?: string } }; message?: string };
    if (withResponse.response?.data?.error) return withResponse.response.data.error;
    if (withResponse.message) return withResponse.message;
  }
  return 'Failed to load financial data';
}

const blankSummary: FinancialSummary = {
  users: null,
  subscriptions: { active: null, trialing: null, pastDue: null },
  collected: { thisMonthCents: null, allTimeCents: null },
  balance: { availableCents: null, pendingCents: null },
  recentPayments: [],
  marshmallowsThisMonthCents: null,
  netThisMonthCents: null,
  payouts: { paidCents: null, next: null },
  stripeError: null,
};

export const FinancialDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, loading: authLoading, logout } = useAuth();
  const [summary, setSummary] = useState<FinancialSummary | null>(null);
  const [plans, setPlans] = useState<any[] | null>(null);
  const [editingPlan, setEditingPlan] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !isAdmin)) {
      navigate('/dashboard');
      return;
    }

    if (!isAuthenticated || !isAdmin) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    subscriptionsApi
      .getFinancialSummary()
      .then(data => {
        if (!cancelled) setSummary(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(errorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    subscriptionsApi
      .getPlans()
      .then(data => {
        if (!cancelled) setPlans(data);
      })
      .catch(() => {
        if (!cancelled) setPlans(null);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, isAuthenticated, isAdmin, authLoading]);

  const savePlan = async (planId: string, data: PlanCatalogUpdate) => {
    const updated = await adminApi.updatePlan(planId, data);
    setPlans(current => (current ?? []).map(plan => (plan.id === planId ? updated : plan)));
    setEditingPlan(null);
  };

  if (authLoading || isLoading) {
    return (
      <div className="page-container flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-12 h-12 mx-auto mb-4 spinner"></div>
          <p className="text-gray-600 font-medium">
            {authLoading ? 'Authenticating...' : 'Loading financial data...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !isAdmin) {
    return null;
  }

  return (
    <div className="page-container">
      <AdminDashboardHeader onLogout={logout} />

      <main className="page-content max-w-7xl mx-auto py-8 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0">
          {(error || summary?.stripeError) && (
            <div className="card p-6 mb-8">
              <p className="text-sm font-medium text-red-600">{error || summary?.stripeError}</p>
            </div>
          )}
          <FinancialSummaryView summary={summary ?? blankSummary} />
          {plans && plans.length > 0 && (
            <div className="mt-8">
              <SubscriptionPlansCard subscriptionStats={{ plans }} onEditPlan={setEditingPlan} />
            </div>
          )}
          {editingPlan && (
            <EditPlanModal
              plan={editingPlan}
              onCancel={() => setEditingPlan(null)}
              onSave={savePlan}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default FinancialDashboard;
